// client/src/api/services/tokenService.ts
import { apiClient } from '../client';
import { ENDPOINTS, buildQueryString } from '../endpoints';

// ==================== TYPES ====================

export interface TokenSummary {
  availableKXT: number;
  walletBalance: number;
  kxtWalletAddress: string | null;
  kxtPendingWithdrawal: number;
  kxtTotalEarned: number;
  kxtTotalWithdrawn: number;
  onChainBalance: string | null;
  lastUpdated: string;
}

export interface TokenTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'claim' | 'withdrawal' | 'kxt_transfer';
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  walletAddress?: string | null;
}

export interface TransactionHistoryResponse {
  transactions: TokenTransaction[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalResults: number;
    resultsPerPage: number;
  };
}

export interface ClaimTokensResponse {
  success: boolean;
  message: string;
  data?: {
    claimedAmount: number;
    newWalletBalance: number;
    transactionId: string;
    timestamp: string;
  };
}

export interface WithdrawTokensResponse {
  success: boolean;
  message: string;
  data?: {
    withdrawalId: string;
    amount: number;
    walletAddress: string;
    status: string;
    estimatedCompletion: string;
    transactionHash: string | null;
  };
}

export interface WithdrawalStatusResponse {
  withdrawalId: string;
  amount: number;
  walletAddress: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  transactionHash: string | null;
  createdAt: string;
  completedAt: string | null;
}

// ==================== TOKEN SERVICE ====================

export const tokenService = {

  // Get token summary (KXT balance, wallet address, totals)
  getTokenSummary: async (): Promise<TokenSummary> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: TokenSummary }>(
        ENDPOINTS.TOKEN.SUMMARY
      );
      return response.data.data;
    } catch (error: unknown) {
      console.error('❌ Failed to get token summary:', error);
      throw error;
    }
  },

  // Claim available KXT rewards into wallet
  claimTokens: async (): Promise<ClaimTokensResponse> => {
    try {
      const response = await apiClient.post<ClaimTokensResponse>(ENDPOINTS.TOKEN.CLAIM, {});
      return response.data;
    } catch (error: unknown) {
      console.error('❌ Failed to claim tokens:', error);
      throw error;
    }
  },

  // Request withdrawal of KXT to external wallet
  withdrawTokens: async (amount: number, walletAddress: string): Promise<WithdrawTokensResponse> => {
    try {
      const response = await apiClient.post<WithdrawTokensResponse>(ENDPOINTS.TOKEN.WITHDRAW, {
        amount,
        walletAddress,
      });
      return response.data;
    } catch (error: unknown) {
      console.error('❌ Failed to withdraw tokens:', error);
      throw error;
    }
  },

  // Get paginated transaction history
  getTransactionHistory: async (
    page: number = 1,
    limit: number = 10,
    type: 'all' | 'claim' | 'withdraw' = 'all',
    search: string = ''
  ): Promise<TransactionHistoryResponse> => {
    try {
      const queryParams = buildQueryString({ page, limit, type, search });
      const url = `${ENDPOINTS.TOKEN.TRANSACTIONS}${queryParams}`;
      const response = await apiClient.get<{ success: boolean; data: TransactionHistoryResponse }>(url);
      return response.data.data;
    } catch (error: unknown) {
      console.error('❌ Failed to get transaction history:', error);
      throw error;
    }
  },

  // Get status of a specific withdrawal
  getWithdrawalStatus: async (withdrawalId: string): Promise<WithdrawalStatusResponse> => {
    try {
      const url = ENDPOINTS.TOKEN.WITHDRAWAL_STATUS(withdrawalId);
      const response = await apiClient.get<{ success: boolean; data: WithdrawalStatusResponse }>(url);
      return response.data.data;
    } catch (error: unknown) {
      console.error('❌ Failed to get withdrawal status:', error);
      throw error;
    }
  },
};

export default tokenService;
